import * as XLSX from "xlsx";

/**
 * Exportação dos relatórios de acompanhamento em CSV, Excel e PDF.
 *
 * Módulo próprio, e não uma extensão do `rel-general.js`: lá as funções de exportação estão presas
 * ao estado do componente dentro de 2.295 linhas. O que veio de lá são os detalhes que aquele
 * arquivo aprendeu na prática — o separador, o BOM, o limite do nome da aba e o negrito do pdfmake.
 *
 * Montar e salvar são separados de propósito: montar é o que dá para conferir fora do navegador.
 *
 * A forma esperada:
 *   { titulo, cabecalho: ["linha de contexto", ...],
 *     secoes: [{ nome, colunas: [{ chave, rotulo, tipo }], linhas: [{ chave: valor }] }] }
 *
 * `tipo` é "texto", "numero" ou "minutos". Minuto sai como número inteiro nos três formatos, com a
 * unidade no cabeçalho da coluna: quem abre a planilha soma a coluna, e quem lê o PDF continua
 * entendendo "210 min". Formatar como "3h30" é coisa da tela.
 */

/** O Excel brasileiro abre CSV por ponto e vírgula; com vírgula, tudo cai numa coluna só. */
const SEPARADOR = ";";

/** Sem o BOM o Excel lê UTF-8 como Latin-1 e todo acento vira caractere quebrado. */
const BOM = "﻿";

/** Limite do Excel para nome de aba. Passar disso não avisa, corta. */
const MAX_NOME_DA_ABA = 31;

function valorDaCelula(linha, coluna) {
  const valor = linha?.[coluna.chave];
  if (valor === null || valor === undefined || valor === "") return null;
  if (coluna.tipo === "numero" || coluna.tipo === "minutos") {
    const n = Number(valor);
    return Number.isFinite(n) ? n : null;
  }
  return String(valor);
}

function paraTexto(valor) {
  return valor === null ? "" : String(valor);
}

export function montarCSV(relatorio) {
  const partes = [];

  (relatorio.cabecalho || []).forEach((linha) => {
    partes.push(`"${String(linha).replace(/"/g, '""')}"`);
  });
  if (relatorio.cabecalho?.length) partes.push("");

  (relatorio.secoes || []).forEach((secao) => {
    partes.push(`"${secao.nome}"`);
    partes.push(secao.colunas.map((c) => `"${c.rotulo}"`).join(SEPARADOR));

    secao.linhas.forEach((linha) => {
      const celulas = secao.colunas.map((coluna) => {
        const valor = valorDaCelula(linha, coluna);
        if (valor === null) return '""';
        // Número sem aspas, senão o Excel trata como texto e a coluna não soma.
        if (typeof valor === "number") return String(valor);
        return `"${valor.replace(/"/g, '""')}"`;
      });
      partes.push(celulas.join(SEPARADOR));
    });

    partes.push("");
  });

  return BOM + partes.join("\n");
}

export function montarPlanilha(relatorio) {
  const workbook = XLSX.utils.book_new();

  (relatorio.secoes || []).forEach((secao, indice) => {
    const grade = [];

    if (indice === 0 && relatorio.cabecalho?.length) {
      relatorio.cabecalho.forEach((linha) => grade.push([linha]));
      grade.push([]);
    }

    grade.push(secao.colunas.map((c) => c.rotulo));
    secao.linhas.forEach((linha) => {
      grade.push(secao.colunas.map((coluna) => valorDaCelula(linha, coluna)));
    });

    const aba = XLSX.utils.aoa_to_sheet(grade);
    aba["!cols"] = secao.colunas.map((coluna) => ({
      wch: coluna.tipo === "texto" ? 24 : 12,
    }));

    XLSX.utils.book_append_sheet(
      workbook,
      aba,
      secao.nome.substring(0, MAX_NOME_DA_ABA),
    );
  });

  return workbook;
}

/** Paleta da marca, a mesma de `src/theme/index.js`. Escrita como valor e não importada de lá
 *  porque o tema guarda os tons secundários em `rgba()`, que o pdfmake não entende. */
const AZUL = "#012443";
const VERMELHO = "#FF3100";
const CINZA_DA_LINHA = "#F2F4F7";
const CINZA_DA_BORDA = "#E1E4E8";
const CINZA_DO_TEXTO = "#5D5F61";

/**
 * Só o documento, sem tocar no pdfmake — é o que deixa o conteúdo ser conferido no Node.
 *
 * O logo entra por parâmetro em vez de import pelo mesmo motivo que o pdfmake ficou de fora: são
 * 36 KB de data URI que não têm o que fazer numa conferência de conteúdo. Sem ele o documento sai
 * inteiro, só com o título ocupando a faixa da marca.
 *
 * `bold: false` em tudo não é descuido: é o que o `rel-general.js` já fazia, porque o bundle de
 * fontes daquela versão do pdfmake não traz o Roboto-Medium e o negrito quebra a geração. Por isso
 * a hierarquia daqui sai de cor e corpo de texto, não de peso.
 */
export function montarDocumentoPDF(relatorio, { logo } = {}) {
  const conteudo = [];

  (relatorio.cabecalho || []).forEach((linha) => {
    conteudo.push({
      text: linha,
      fontSize: 9,
      color: CINZA_DO_TEXTO,
      margin: [0, 0, 0, 2],
    });
  });

  (relatorio.secoes || []).forEach((secao) => {
    conteudo.push({
      text: secao.nome,
      fontSize: 13,
      bold: false,
      color: AZUL,
      margin: [0, 16, 0, 8],
    });

    const cabecalho = secao.colunas.map((coluna) => ({
      text: coluna.rotulo,
      style: "cabecalhoDaTabela",
    }));

    const corpo = secao.linhas.map((linha) =>
      secao.colunas.map((coluna) => paraTexto(valorDaCelula(linha, coluna))),
    );

    conteudo.push({
      table: {
        headerRows: 1,
        widths: secao.colunas.map((coluna) =>
          coluna.tipo === "texto" ? "*" : "auto",
        ),
        body: [cabecalho, ...corpo],
      },
      layout: {
        hLineWidth: (i, node) =>
          i === 0 || i === 1 || i === node.table.body.length ? 1 : 0.5,
        vLineWidth: () => 0.5,
        hLineColor: (i, node) =>
          i === 0 || i === 1 || i === node.table.body.length
            ? AZUL
            : CINZA_DA_BORDA,
        vLineColor: () => CINZA_DA_BORDA,
        // Zebra a partir da primeira linha de dado; a 0 é o cabeçalho, que se pinta sozinho.
        fillColor: (i) => (i > 0 && i % 2 === 0 ? CINZA_DA_LINHA : null),
      },
      margin: [0, 0, 0, 16],
    });
  });

  return {
    pageSize: "A4",
    pageOrientation: "landscape",
    pageMargins: [28, 64, 28, 52],
    header: (pagina, total, tamanhoDaPagina) => {
      const faixa = [];
      if (logo) faixa.push({ image: logo, width: 88 });
      faixa.push({
        text: relatorio.titulo,
        fontSize: 14,
        bold: false,
        color: AZUL,
        alignment: logo ? "right" : "left",
        margin: [0, logo ? 4 : 0, 0, 0],
      });

      return {
        margin: [28, 22, 28, 0],
        stack: [
          { columns: faixa },
          {
            canvas: [
              {
                type: "line",
                x1: 0,
                y1: 8,
                // As margens laterais da página, que o filete atravessa de ponta a ponta.
                x2: tamanhoDaPagina.width - 56,
                y2: 8,
                lineWidth: 1.5,
                lineColor: VERMELHO,
              },
            ],
          },
        ],
      };
    },
    content: conteudo,
    footer: (pagina, total) => ({
      margin: [28, 14, 28, 0],
      columns: [
        {
          width: "*",
          stack: [
            { text: "Fortio Transportes Colaborativos LTDA", fontSize: 8 },
            { text: "www.fortio.com.br", fontSize: 8, color: CINZA_DO_TEXTO },
          ],
        },
        {
          width: "auto",
          text: `Página ${pagina} de ${total}`,
          fontSize: 8,
          color: CINZA_DO_TEXTO,
          alignment: "right",
          margin: [0, 5, 0, 0],
        },
      ],
    }),
    styles: {
      cabecalhoDaTabela: {
        bold: false,
        fontSize: 9,
        color: "#FFFFFF",
        fillColor: AZUL,
      },
    },
    defaultStyle: { fontSize: 9, color: "#1E2124" },
  };
}

export function nomeDoArquivo(relatorio, extensao) {
  const dia = new Date().toISOString().slice(0, 10);
  return `${relatorio.titulo.replace(/\s+/g, "_")}_${dia}.${extensao}`;
}

export function baixarCSV(relatorio) {
  const blob = new Blob([montarCSV(relatorio)], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = nomeDoArquivo(relatorio, "csv");
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function baixarPlanilha(relatorio) {
  XLSX.writeFile(montarPlanilha(relatorio), nomeDoArquivo(relatorio, "xlsx"));
}
